// Instructional banner shown above form sections
// Full state: explains purpose + status system + key questions; dismissed: renders nothing
// Set ENABLED to true to re-enable the banner
import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

const DISMISSED_KEY = 'startus:form-instruction-dismissed';
const ENABLED = false;

interface FormInstructionBannerProps {
  completionPercent: number;
}

export function FormInstructionBanner({ completionPercent }: FormInstructionBannerProps) {
  const { t } = useTranslation('common');

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // ignore storage errors
    }
    setDismissed(true);
  };

  if (!ENABLED || dismissed) return null;

  const keyQuestions = t('formInstructionBanner.keyQuestions', { returnObjects: true }) as string[];

  return (
    <div className="bg-[#1a1a1a] border border-white/5 rounded-xl px-5 py-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-3">
          {/* Main instructional text with dynamic % */}
          <p className="text-white font-medium leading-relaxed">
            <Trans
              i18nKey="formInstructionBanner.heading"
              ns="common"
              values={{ percent: completionPercent }}
              components={{
                percent: <span className="text-[#20BD5A]" />,
              }}
            />
          </p>

          {/* Status dropdown explanation with highlighted "Validated Answer" */}
          <p className="text-sm text-gray-400 leading-relaxed">
            <Trans
              i18nKey="formInstructionBanner.dropdownExplanation"
              ns="common"
              components={{
                validated: <span className="text-[#20BD5A] font-medium" />,
              }}
            />
          </p>

          {/* Key questions list */}
          <div className="space-y-2">
            <p className="text-sm text-gray-500">{t('formInstructionBanner.keyQuestionsLabel')}</p>
            <ul className="space-y-1.5">
              {keyQuestions.map((question) => (
                <li key={question} className="flex items-start gap-2 text-sm text-gray-400">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#20BD5A] shrink-0" />
                  {question}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className="shrink-0 text-xs text-gray-500 hover:text-white border border-white/10 hover:border-white/20 rounded-lg px-3 py-1.5 transition-colors"
        >
          {t('formInstructionBanner.dismiss')}
        </button>
      </div>
    </div>
  );
}
